import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2RevenueAccessContextResponse,
  AdminV2RevenueDisputeDetailResponse,
  AdminV2RevenueDisputesResponse,
  AdminV2RevenueMrrScoreboardResponse,
  AdminV2RevenueSubscriptionsResponse,
  AdminV2RevenueTransactionDetailResponse,
  AdminV2RevenueTransactionsResponse,
  AdminV2RevenueWebhookEventsResponse,
} from "@/types/adminV2";

export const buildAdminV2RevenueReadEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2RevenueAccessContext: builder.query<
    AdminV2RevenueAccessContextResponse,
    { organisationId?: string } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/access-context",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueAccess", id: "CONTEXT" }],
    // Keep shared menu visibility warm across route switches so the shell
    // does not pay an extra access-context round trip on every Revenue tab.
    keepUnusedDataFor: 300,
  }),

  getAdminV2RevenueMrrScoreboard: builder.query<
    AdminV2RevenueMrrScoreboardResponse,
    void
  >({
    query: () => ({
      url: "/admin/v2/revenue/mrr-scoreboard",
    }),
    providesTags: [{ type: "AdminV2RevenueMrrScoreboard", id: "HEADLINE" }],
    keepUnusedDataFor: 300,
  }),

  getAdminV2RevenueTransactions: builder.query<
    AdminV2RevenueTransactionsResponse,
    {
      organisationId?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/transactions",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueTransactions", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2RevenueTransactionDetail: builder.query<
    AdminV2RevenueTransactionDetailResponse,
    { transactionId: string }
  >({
    query: ({ transactionId }) => ({
      url: `/admin/v2/revenue/transactions/${transactionId}`,
    }),
    providesTags: (_result, _error, { transactionId }) => [
      { type: "AdminV2RevenueTransactions", id: transactionId },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2RevenueSubscriptions: builder.query<
    AdminV2RevenueSubscriptionsResponse,
    {
      organisationId?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/subscriptions",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueSubscriptions", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2RevenueDisputes: builder.query<
    AdminV2RevenueDisputesResponse,
    {
      organisationId?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/disputes",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueDisputes", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2RevenueDisputeDetail: builder.query<
    AdminV2RevenueDisputeDetailResponse,
    { disputeId: string }
  >({
    query: ({ disputeId }) => ({
      url: `/admin/v2/revenue/disputes/${disputeId}`,
    }),
    providesTags: (_result, _error, { disputeId }) => [
      { type: "AdminV2RevenueDisputes", id: disputeId },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2RevenueWebhookEvents: builder.query<
    AdminV2RevenueWebhookEventsResponse,
    {
      organisationId?: string;
      status?: "queued" | "processing" | "processed" | "failed";
      eventType?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/webhooks",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueTransactions", id: "WEBHOOKS" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2RevenueWebhookEventDetail: builder.query<
    { event: AdminV2RevenueWebhookEventsResponse["events"][number] },
    { webhookEventId: string }
  >({
    query: ({ webhookEventId }) => ({
      url: `/admin/v2/revenue/webhooks/${webhookEventId}`,
    }),
    providesTags: (_result, _error, { webhookEventId }) => [
      {
        type: "AdminV2RevenueTransactions",
        id: `WEBHOOK:${webhookEventId}`,
      },
    ],
    keepUnusedDataFor: 10,
  }),
});
